import { hasTrackingStatus, isDeliveredStatus } from './sparePartsStatusColors'

/**
 * Availability and shipment status of a Spare Part Sales line (IS2-177, IS2-240).
 *
 * The backend sends both values already resolved on every line. The rules are repeated here
 * for two reasons: the screen must read correctly while a backend without these fields is
 * still deployed, and a line with a tracking number must never read as Not shipped, whatever
 * a stale payload says.
 */
export const LineAvailability = {
	BACKORDERED: 'Backordered',
	PROCESSING: 'Processing',
	RELEASED: 'Released'
}

export const ShipmentStatus = {
	NOT_SHIPPED: 'NotShipped',
	IN_TRANSIT: 'InTransit',
	DELIVERED: 'Delivered'
}

const MEXICO_WAREHOUSE = 1

const SHIPMENT_STATUS_LABELS = {
	[ShipmentStatus.NOT_SHIPPED]: 'Not shipped',
	[ShipmentStatus.IN_TRANSIT]: 'In transit',
	[ShipmentStatus.DELIVERED]: 'Delivered'
}

export const availabilityOptions = [
	{ value: LineAvailability.BACKORDERED, label: LineAvailability.BACKORDERED },
	{ value: LineAvailability.PROCESSING, label: LineAvailability.PROCESSING },
	{ value: LineAvailability.RELEASED, label: LineAvailability.RELEASED }
]

/**
 * The availability a line shows: the value the backend sent (a manual adjustment, kept even
 * when the line has a tracking number); otherwise Released when the line has a tracking number;
 * otherwise the default rule — Mexico warehouse is Backordered, any other warehouse is Released.
 * No rule reaches Processing: only a manual adjustment sets it (IS2-240).
 *
 * @param {object} item the spare-part line item
 * @returns {string} one of LineAvailability
 */
export const getLineAvailability = (item) => {
	if (Object.values(LineAvailability).includes(item && item.Availability)) return item.Availability
	if (hasTrackingStatus(item)) return LineAvailability.RELEASED
	return item && item.Warehouse === MEXICO_WAREHOUSE ? LineAvailability.BACKORDERED : LineAvailability.RELEASED
}

/**
 * The grouped shipment status of a line. No tracking number is Not shipped, whatever the payload
 * says; with one, the backend value wins when present, and otherwise the carrier status decides:
 * delivered is Delivered and anything else — including no status yet — is In transit.
 *
 * @param {object} item the spare-part line item
 * @returns {string} one of ShipmentStatus
 */
export const getShipmentStatus = (item) => {
	if (!hasTrackingStatus(item)) return ShipmentStatus.NOT_SHIPPED
	if (item.ShipmentStatus === ShipmentStatus.IN_TRANSIT || item.ShipmentStatus === ShipmentStatus.DELIVERED) return item.ShipmentStatus
	return isDeliveredStatus(item.Status) ? ShipmentStatus.DELIVERED : ShipmentStatus.IN_TRANSIT
}

export const getShipmentStatusLabel = (shipmentStatus) => SHIPMENT_STATUS_LABELS[shipmentStatus]
